import clsx from "clsx";
import InputLabel from "components/InputLabel";
import Tooltip from "components/Tooltip";
import useInvalidInput from "hooks/useInvalidInput";
import useSwitch from "hooks/useSwitch";
import React from "react";

interface TextInputProps extends React.HTMLProps<HTMLInputElement> {
  unit?: string | React.ReactNode;
  labelName?: string;
  containerClass?: string;
  enforceRequired?: boolean;
  handleChange?: (value: string, name?: string) => void | Promise<void>;
  errorMessage?: string;
  noMargin?: boolean;
  hasError?: boolean;
  testID?: string;
  showMandatoryAlert?: boolean;
  height?: "large" | "middle" | "small";
  tooltip?: string;
  helper?: React.ReactNode;
  min?: number;
}

export default function TextInput(props: TextInputProps) {
  const [showError, switchShowError] = useSwitch(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
    if (props.required) {
      if (e.target.value.length === 0) switchShowError.on();
      else switchShowError.off();
    }
    props.onBlur && props.onBlur(e);
  }

  function handleChange(e: React.FocusEvent<HTMLInputElement>) {
    let value = e.target.value;
    if (props.type === "integer") {
      value = value.replace(/[^\d]/g, "");
    }

    if (props.type === "number") {
      value = value.replace(/[^0-9.,-]/g, "");
      value = value.replace(",", ".");
    }

    const isEmpty = value.length === 0;
    if (props.required && showError && !isEmpty) {
      switchShowError.off();
    }
    if (props.required && !showError && isEmpty) {
      switchShowError.on();
    }

    props.onChange && props.onChange(e);

    const validatedValue =
      (value.includes("-") && Number(props.min) > 0) ||
      (value === "0" && Number(props.min) > 0)
        ? ""
        : value;

    props.handleChange?.(validatedValue, props.name);
  }

  useInvalidInput(inputRef, (isInvalid) => {
    if (isInvalid) {
      switchShowError.on();
    } else {
      switchShowError.off();
    }
  });

  function validateValue(): string | number | readonly string[] | undefined {
    return (props.min && Number(props.value) < Number(props.min)) ||
      (props.max && Number(props.value) > Number(props.max)) ||
      (Number(props.min) > 0 && String(props.value).includes("-")) ||
      (Number(props.min) > 0 && props.value === 0)
      ? ""
      : props.value;
  }
  const hasValidationError =
    props.required &&
    (!String(props.value)?.length ||
      props.value === "undefined" ||
      props.value === "null") &&
    props.showMandatoryAlert;

  const handleKeyDown = (e: { key: string; preventDefault: () => void }) => {
    if (Number(props.min) >= 0 && (e.key === "-" || e.key === "e")) {
      e.preventDefault();
    }
  };

  return (
    <div
      style={props.style}
      className={`pb-15 ${props.noMargin ? "mb-0" : "mb-4"} ${
        props.containerClass ?? ""
      }`}
    >
      {props.labelName && (
        <div className="flex items-center">
          <InputLabel
            required={props.required}
            labelName={props.labelName}
            name={props.name as string}
          />
          {props.tooltip && (
            <Tooltip
              content={props.tooltip ?? ""}
              className="mb-1 text-gray-500"
            />
          )}
        </div>
      )}
      <div className="relative flex items-center w-[100%] mx-auto overflow-hidden">
        <input
          ref={inputRef}
          min={props.min}
          max={props.max}
          value={validateValue()}
          step={props.step}
          data-invalid="false"
          placeholder={props.placeholder}
          name={props.name}
          defaultValue={props.defaultValue}
          required={props.required && props.enforceRequired !== false}
          disabled={props.disabled}
          key={`text-input-key-${props.key ?? ""}-${props.defaultValue}`}
          onInvalid={() => switchShowError.on()}
          type={props.type === "number" ? "text" : props.type}
          inputMode={props.type === "number" ? "decimal" : undefined}
          onChange={handleChange}
          onClick={props.onClick}
          test-id={props.testID}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={clsx(
            "appearance-none block w-full px-2 py-[7px] border border-gray-300 rounded-md",
            "shadow-sm placeholder-gray-500",
            "text-sm appearance-none focus:border-indigo-500 focus-visible:outline-none",
            {
              "opacity-50": props.disabled,
              "border-red-500 focus:border-red-500 placeholder:text-red-500":
                props.showMandatoryAlert != undefined
                  ? hasValidationError
                  : showError || props.hasError,
              "pr-12": props.unit,
              "h-large": props.height === "large",
              "h-middle": props.height === "middle",
              "h-small": props.height === "small",
            },
            props.className
          )}
        />
        {props.unit && (
          <span
            className={clsx(
              "absolute right-[1px] flex items-center rounded-tr-md rounded-br-md",
              "bg-gray-100 border border-transparent h-[94%] px-2 text-sm text-gray-400 border-l-gray-300",
              {
                "text-red-700 border-l-red-500":
                  props.showMandatoryAlert != undefined
                    ? hasValidationError
                    : showError || props.hasError,
              }
            )}
          >
            {props.unit}
          </span>
        )}
        {props.children}
      </div>
      {props.required && props.errorMessage && hasValidationError && (
        <label className="text-red-600 text-sm font-normal mt-2">
          {props.errorMessage}
        </label>
      )}
      {props.helper && (
        <div
          className={clsx("text-sm leading-5 font-normal mt-2 text-gray-500", {
            "text-red-600": hasValidationError,
          })}
        >
          {props.helper}
        </div>
      )}
    </div>
  );
}
